import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { NzMessageService } from 'ng-zorro-antd/message';
import { BehaviorSubject, Subscription } from 'rxjs';

import { ActionService, cleanObject, CustomFormatter, ISitemap, ISitemapCommand, ISitemapModel, SITEMAP } from 'pbc.angular';

import { IPostStandortResponse, IStandort } from 'fa-kt.types';
import { StandortsService } from '../../../query';
import { PostStandortCommandService } from '../service';

interface IFormValue {
  standort?: IStandort;
}

@Component({
  selector: 'fa-kt-post-standort',
  templateUrl: './post-standort.component.html',
  styleUrls: ['./post-standort.component.css'],
})
export class PostStandortCommandComponent implements OnInit, OnDestroy {
  command!: ISitemapCommand;
  model!: ISitemapModel;

  subscriptions: Subscription[] = [];
  form!: FormGroup;
  $loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  $valid: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  CustomFormatter = CustomFormatter;

  @Input() set loading(loading: boolean) {
    this.$loading.next(this.$loading.getValue() || loading);
  }
  @Input() set value(value: IFormValue) {
    this.patch(value);
  }
  @Output() valid: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() finished: EventEmitter<IPostStandortResponse> = new EventEmitter<IPostStandortResponse>();

  constructor(
    @Inject(SITEMAP) private sitemap: ISitemap,
    private fb: FormBuilder,
    private actions: ActionService,
    private message: NzMessageService,
    public standorts: StandortsService,
    public postStandort: PostStandortCommandService /* << dependencies    */ /*    dependencies >> */,
  ) {
    this.command = sitemap.PERSONEN.Commands.POST_STANDORT;
    this.model = sitemap.PERSONEN.Models.STANDORT;
    /* << constructor    */
    /*    constructor >> */
    this.form = this.fb.group({
      standort: this.fb.group({
        id: [null, []],
        name: [null, [Validators.required]],
        addresse: [],
        dateipfad: [null, []],
      }),
    });
    this.patch();
  }

  ngOnInit() {
    this.subscriptions.push(
      this.form.valueChanges.subscribe((value: any) => {
        this.valid.emit(this.form.valid);
        if (this.form.valid) {
          this.actions.subscribe({ key: this.command.translation, action: async () => await this.submit() });
        } else {
          this.actions.unsubscribe(this.command.translation);
        }
      }),
      this.id?.valueChanges.subscribe(async (id) => {
        /* << on-id-change    */
        /*    on-id-change >> */
      }) as Subscription,
      this.name?.valueChanges.subscribe(async (name) => {
        /* << on-name-change    */
        /*    on-name-change >> */
      }) as Subscription,
      this.addresse?.valueChanges.subscribe(async (addresse) => {
        /* << on-addresse-change    */
        /*    on-addresse-change >> */
      }) as Subscription,
      this.dateipfad?.valueChanges.subscribe(async (dateipfad) => {
        /* << on-dateipfad-change    */
        /*    on-dateipfad-change >> */
      }) as Subscription,
      /* << subscibe    */
      /*    subscibe >> */
    );
    /* << init    */
    /*    init >> */
  }

  ngOnDestroy() {
    this.actions.unsubscribe(this.command.translation);
    this.subscriptions.filter(($: Subscription) => $ && !$.closed).forEach(($: Subscription) => $.unsubscribe());
  }

  private patch(value?: IFormValue) {
    let standort = {
      id: null,
      name: null,
      addresse: null,
      dateipfad: null,
    };
    if (value && value.standort) {
      standort = {
        ...standort,
        ...cleanObject(value.standort),
      };
    }
    this.form.patchValue({ standort });
    /* << custom-stuff    */
    /*    custom-stuff >> */
    this.update();
  }

  update() {
    this.$loading.next(true);
    this.form.markAsDirty();
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
    this.$valid.next(this.form.valid);
    this.$loading.next(false);
  }

  async submit() {
    this.update();
    if (!this.form.valid) {
      return;
    }
    this.$loading.next(true);
    try {
      const payload = cleanObject(this.form.getRawValue());
      if (payload && payload.standort) {
        payload.standort = cleanObject(payload.standort);
      }
      const response: IPostStandortResponse | undefined = await this.postStandort.request(payload);
      this.finished.next(response);
    } catch (error: any) {
      this.message.error(error.message);
      for (const validation of error.errors ? error.errors : []) {
        for (const children of validation.children ? validation.children : []) {
          const message = Object.values(children.constraints).reduce((acc, val) => acc + ' ' + val, '');
          this.form.controls.objektArt.get(children.property)?.setErrors({ server: message });
        }
      }
    }
    this.$loading.next(false);
  }

  get id() {
    return this.form.controls.standort.get('id');
  }

  get name() {
    return this.form.controls.standort.get('name');
  }

  get addresse() {
    return this.form.controls.standort.get('addresse');
  }

  get dateipfad() {
    return this.form.controls.standort.get('dateipfad');
  }
}
